import { useEffect, useRef, useState } from 'react';

const staffMembers = [
  {
    name: '田中 恵子',
    role: '施設長 / 介護福祉士',
    message:
      '「ここに来てよかった」と思っていただける場所をつくることが、私たちの日々の目標です。スタッフ一同、誠実なケアを心がけています。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20female%20care%20facility%20manager%20professional%20portrait%20warm%20smile%20white%20uniform%20clean%20background%20soft%20natural%20light%20trustworthy%20expression%2040s%20woman%20professional%20headshot%20indoor%20studio%20light&width=400&height=500&seq=staff001&orientation=portrait',
    qualifications: ['介護福祉士', '社会福祉主事'],
  },
  {
    name: '山本 健太',
    role: '機能訓練指導員 / 理学療法士',
    message:
      '利用者の方が「できた」と感じる瞬間を大切にしています。無理のない範囲で、その方のペースに合わせた訓練を提供します。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20male%20physical%20therapist%20professional%20portrait%20warm%20smile%20light%20blue%20uniform%20clean%20background%20soft%20natural%20light%20trustworthy%20expression%2030s%20man%20professional%20headshot%20indoor%20studio%20light&width=400&height=500&seq=staff002&orientation=portrait',
    qualifications: ['理学療法士', '機能訓練指導員'],
  },
  {
    name: '佐藤 美咲',
    role: '介護士 / 介護福祉士',
    message:
      '毎日の小さな変化に気づき、ご利用者の気持ちに寄り添うことを大切にしています。笑顔でお迎えできるよう努めています。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20female%20caregiver%20professional%20portrait%20warm%20smile%20pink%20uniform%20clean%20background%20soft%20natural%20light%20kind%20expression%2020s%20woman%20professional%20headshot%20indoor%20studio%20light&width=400&height=500&seq=staff003&orientation=portrait',
    qualifications: ['介護福祉士', '認知症ケア専門士'],
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function StaffSection() {
  const { ref, inView } = useInView();
  const { ref: facilityRef, inView: facilityInView } = useInView();

  return (
    <section id="staff" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div ref={ref} className="text-center mb-16">
          <div
            className={`inline-flex items-center gap-2 mb-4 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="h-px w-8 bg-[#88B277]" />
            <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Our Staff</span>
            <div className="h-px w-8 bg-[#88B277]" />
          </div>
          <h2
            className={`text-3xl md:text-4xl font-bold text-gray-800 mb-4 transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            スタッフ紹介
          </h2>
          <p
            className={`text-gray-500 text-base max-w-xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            専門資格を持つスタッフが、ご利用者おひとりおひとりに誠実に向き合います。
          </p>
        </div>

        {/* Staff Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {staffMembers.map((staff, i) => (
            <div
              key={staff.name}
              className={`transition-all duration-700 ${
                inView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
              }`}
              style={{ transitionDelay: `${300 + i * 150}ms` }}
            >
              <div className="relative overflow-hidden rounded-2xl mb-5">
                <img
                  src={staff.image}
                  alt={`${staff.name} - ${staff.role}`}
                  className="w-full h-72 object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5">
                  {staff.qualifications.map((q) => (
                    <span
                      key={q}
                      className="px-2.5 py-1 bg-[#88B277]/90 text-white text-xs font-medium rounded-full"
                    >
                      {q}
                    </span>
                  ))}
                </div>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-0.5">{staff.name}</h3>
              <p className="text-[#88B277] text-sm font-medium mb-3">{staff.role}</p>
              <div className="relative pl-4">
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#88B277]/30 rounded-full" />
                <p className="text-gray-500 text-sm leading-relaxed">{staff.message}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Facility Gallery */}
        <div ref={facilityRef}>
          <div
            className={`text-center mb-10 transition-all duration-700 ${
              facilityInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-px w-8 bg-[#88B277]" />
              <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Facility</span>
              <div className="h-px w-8 bg-[#88B277]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">施設のご紹介</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              {
                src: 'https://readdy.ai/api/search-image?query=Japanese%20dayservice%20center%20bright%20spacious%20common%20room%20tatami%20area%20comfortable%20chairs%20natural%20light%20windows%20plants%20clean%20modern%20interior%20warm%20atmosphere%20no%20people&width=400&height=300&seq=fac001&orientation=landscape',
                alt: '共有スペース',
                label: '共有スペース',
              },
              {
                src: 'https://readdy.ai/api/search-image?query=Japanese%20care%20facility%20dining%20room%20round%20tables%20chairs%20bright%20clean%20modern%20interior%20natural%20light%20warm%20colors%20flowers%20on%20tables%20no%20people%20welcoming%20atmosphere&width=400&height=300&seq=fac002&orientation=landscape',
                alt: '食堂',
                label: '食堂',
              },
              {
                src: 'https://readdy.ai/api/search-image?query=Japanese%20rehabilitation%20room%20physical%20therapy%20equipment%20parallel%20bars%20exercise%20mats%20bright%20clean%20modern%20facility%20natural%20light%20professional%20medical%20equipment%20no%20people&width=400&height=300&seq=fac003&orientation=landscape',
                alt: '機能訓練室',
                label: '機能訓練室',
              },
              {
                src: 'https://readdy.ai/api/search-image?query=Japanese%20care%20facility%20accessible%20bathroom%20bathing%20room%20clean%20modern%20safety%20equipment%20grab%20bars%20bright%20white%20tiles%20warm%20lighting%20professional%20accessible%20design%20no%20people&width=400&height=300&seq=fac004&orientation=landscape',
                alt: '浴室',
                label: '浴室',
              },
            ].map((item, i) => (
              <div
                key={item.label}
                className={`relative overflow-hidden rounded-xl group cursor-pointer transition-all duration-700 ${
                  facilityInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
                }`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-40 md:h-52 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
